import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import Utils from './util'
import MessageModal from '../components/MessageModal/MessageModal'

/*
 * Set a global version from package.json so we can later
 * compare with the app version and reload if necessary to bust the cache
 */
import packageJson from '../../../package.json'
import useHome from '../hooks/useHome.js'
window.appVersion = packageJson.version
console.log('LiftOne Now v.', window.appVersion)

const clearDeployCaches = async () => {
    try {
        // Clear service worker/browser caches that can hold old deploy assets.
        if ('caches' in window) {
            const cacheNames = await caches.keys()
            await Promise.all(
                cacheNames.map(cacheName => caches.delete(cacheName)),
            )
        }
    } catch (error) {
        console.error('Error clearing deploy caches:', error)
    }
}

const refreshCacheAndReload = async () => {
    console.log('Clearing cache and reloading...')
    // Update global version
    window.appVersion = packageJson.version

    // Clear deploy caches without removing the user's session from localStorage.
    await clearDeployCaches()

    // Force reload from server, not from cache
    const url = new URL(window.location.href)
    url.searchParams.set('reload', Date.now().toString())
    window.location.replace(url.toString())
}

const VersionCheck = ({ children, history }) => {
    const [isLoading, setIsLoading] = useState(true)
    const [isLatestVersion, setIsLatestVersion] = useState(true)
    const [isReloading, setIsReloading] = useState(false)
    const { handleCheckVersion } = useHome()

    const onCheckedVersion = response => {
        let meta = response.data
        const latestVersion = meta.version
        const currentVersion = window.appVersion

        const shouldForceRefresh = Utils.semverGreaterThan(
            latestVersion,
            currentVersion,
        )
        if (shouldForceRefresh) {
            console.log(`We have a new version: ${latestVersion}`)
            setIsLoading(false)
            setIsLatestVersion(false)
        } else {
            setIsLoading(false)
            setIsLatestVersion(true)
        }
    }

    const checkVersion = () => {
        setIsLoading(true)
        // Add the ?nc=[time in milliseconds] to prevent caching the response
        handleCheckVersion(onCheckedVersion)
    }

    useEffect(() => {
        const url = new URL(window.location.href)
        if (url.searchParams.has('reload')) {
            window.history.replaceState({}, '', window.location.pathname)
        }
    }, [])

    useEffect(() => {
        // Check once on mount
        checkVersion()

        // Setup later checks on each route change
        const stopListeningToRouteChanges = history.listen(() => {
            checkVersion()
        })

        return () => {
            // Remove listener
            stopListeningToRouteChanges()
        }
    }, [])

    const handleRefreshCacheAndReload = async () => {
        if (isReloading) {
            return
        }

        setIsReloading(true)
        await refreshCacheAndReload()
    }

    const newVersionModal =
        !isLoading && !isLatestVersion
            ? React.createElement(MessageModal, {
                  isOpen: true,
                  title: 'New version available',
                  message:
                      'A new version of LiftOne Now is available. Please reload the app to continue.',
                  onDone: handleRefreshCacheAndReload,
                  setIsOpen: handleRefreshCacheAndReload,
                  textDone: isReloading ? 'Reloading...' : 'Reload',
              })
            : null

    return React.createElement(React.Fragment, null, children, newVersionModal)
}

VersionCheck.propTypes = {
    children: PropTypes.node,
    history: PropTypes.shape({
        listen: PropTypes.func,
    }),
}

export default withRouter(VersionCheck)
